import { ValuePropositionsAction } from './actions';
import * as ActionType from './constants';

const INIT_STATE = {
    vpList: null,
    vpListLoading: false,
    vpListError: null,
    duplicating: false,
    duplicateError: null,
    vp: null,
    vpRequestedId: null,
    vpLoading: false,
    vpError: null,
    vpFailure: null,
    saving: false,
    // Save-succeeded signal: null until SAVE_VALUE_PROPOSITION_SUCCESS, reset when a save starts.
    savingResult: null,
    deleting: false,
    changingActiveState: false,
    followers: null,
    followersLoading: false,
    followersError: null,
    Attachments: null,
    AttachmentsLoading: false,
    AttachmentsError: null,
    sharedVp: null,
    sharedVpLoading: false,
    sharedVpError: null,
    preparingForDowngrade: false,
    prepareForDowngradeError: null,
    userOwnedVPs: null,
    userOwnedVPsLoading: false,
    userOwnedVPsError: null,
    updatingVPKeepStatus: false,
    updateVPKeepStatusError: null,
};

type State = {
    vpList?: {};
    vpListLoading: boolean;
    vpListError?: string;
    duplicating?: boolean;
    duplicateError?: string;
    vp?: {};
    vpRequestedId?: number;
    vpLoading?: boolean;
    vpError?: string;
    vpFailure?: { status: number, body?: any };
    saving?: boolean;
    savingResult?: {};
    deleting?: boolean;
    changingActiveState?: Boolean;
    followers?: {},
    followersLoading?: boolean;
    followersError?: string;
    attachments?: {},
    attachmentsLoading?: boolean;
    attachmentsError?: string;
};

const ValueProposition = (state: State = INIT_STATE, action: ValuePropositionsAction) => {
    switch (action.type) {
        case ActionType.CLEAR_SELECTED_VALUE_PROPOSITION:
            // Also forget which VP was fetched, so a page still open (e.g. Edit VP
            // while it exits) waits instead of taking the cleared VP as fetched.
            return { ...state, vp: null, vpRequestedId: null, vpFailure: null };
        case ActionType.CLEAR_VALUE_PROPOSITION_LIST:
            return { ...state, vpList: null };
        case ActionType.LOAD_VALUE_PROPOSITIONS_LIST:
            return { ...state, vpList: [], vpListLoading: true, vpListError: null };
        case ActionType.LOAD_VALUE_PROPOSITIONS_LIST_SUCCESS:
            return { ...state, vpList: action.payload, vpListLoading: false, vpListError: null };
        case ActionType.LOAD_VALUE_PROPOSITIONS_LIST_FAILED:
            return { ...state, vpListError: action.payload, vpListLoading: false };
        case ActionType.DUPLICATE_VALUE_PROPOSITION:
            return { ...state, duplicating: true, duplicateError: null };
        case ActionType.DUPLICATE_VALUE_PROPOSITION_SUCCESS:
            return { ...state, duplicating: false };
        case ActionType.DUPLICATE_VALUE_PROPOSITION_FAILED:
            return { ...state, duplicating: false, duplicateError: action.payload };

        case ActionType.FETCH_VALUE_PROPOSITION_BY_SHARED_KEY:
        case ActionType.FETCH_VALUE_PROPOSITION_BY_SHARE_KEY:
            return { ...state, sharedVp: null, sharedVpLoading: true, sharedVpError: null };
        case ActionType.FETCH_VALUE_PROPOSITION_BY_SHARED_KEY_SUCCESS:
            return { ...state, sharedVpLoading: false, sharedVp: action.payload.vp, sharedVpError: null };
        case ActionType.FETCH_VALUE_PROPOSITION_BY_SHARED_KEY_FAILED:
            return { ...state, sharedVpLoading: false, sharedVpError: action.payload };

        case ActionType.FETCH_VALUE_PROPOSITION_BY_ID:
            return {
                ...state,
                vp: null,
                vpRequestedId: action.payload.vpId,
                vpLoading: true,
                vpError: null,
                vpFailure: null,
            };
        case ActionType.FETCH_VALUE_PROPOSITION_BY_ID_SUCCESS:
            return { ...state, vpLoading: false, vp: action.payload.vp };
        case ActionType.FETCH_VALUE_PROPOSITION_BY_ID_FAILED:
            return { ...state, vpLoading: false, vpError: action.payload, vpFailure: action.failure };
        case ActionType.SAVE_VALUE_PROPOSITION:
            return { ...state, savingResult: null, saving: true };
        case ActionType.SAVE_VALUE_PROPOSITION_SUCCESS:
            return {
                ...state,
                savingResult: { vpId: action.payload.vpId, clientId: action.payload.clientId },
                saving: false,
            };
        case ActionType.SAVE_VALUE_PROPOSITION_FAILED:
            // The saga toasts the reason; the form only needs to know the save ended.
            return { ...state, saving: false, savingResult: null };

        case ActionType.CHANGE_VALUE_PROPOSITION_STATE:
            return { ...state, changingActiveState: true, vpError: null };
        case ActionType.CHANGE_VALUE_PROPOSITION_STATE_SUCCESS:
            return { ...state, changingActiveState: false };
        case ActionType.CHANGE_VALUE_PROPOSITION_STATE_FAILED:
            return { ...state, changingActiveState: false, vpError: action.payload };
        case ActionType.DELETE_VALUE_PROPOSITION:
            return { ...state, deleting: true, vpError: null };
        case ActionType.DELETE_VALUE_PROPOSITION_SUCCESS:
            return { ...state, deleting: false };
        case ActionType.DELETE_VALUE_PROPOSITION_FAILED:
            return { ...state, deleting: false, vpError: action.payload };

        case ActionType.FETCH_FOLLOWERS_BY_VALUE_PROPOSITION_ID:
            return { ...state, followers: null, followersError: null, followersLoading: true };
        case ActionType.FETCH_FOLLOWERS_BY_VALUE_PROPOSITION_ID_SUCCESS:
            return { ...state, followers: action.payload.followers, followersLoading: false };
        case ActionType.FETCH_FOLLOWERS_BY_VALUE_PROPOSITION_ID_FAILED:
            return { ...state, followersError: action.payload, followersLoading: false };
        
        case ActionType.FETCH_ATTACHMENTS_BY_VALUE_PROPOSITION_ID:
            return { ...state, attachments: null, attachmentsError: null, attachmentsLoading: true };
        case ActionType.FETCH_ATTACHMENTS_BY_VALUE_PROPOSITION_ID_SUCCESS:
            return { ...state, attachments: action.payload.attachments, attachmentsLoading: false };
        case ActionType.FETCH_ATTACHMENTS_BY_VALUE_PROPOSITION_ID_FAILED:
            return { ...state, attachmentsError: action.payload, attachmentsLoading: false };
        
        case ActionType.PREPARE_FOR_DOWNGRADE:
            return { ...state, preparingForDowngrade: true, prepareForDowngradeError: null };
        case ActionType.PREPARE_FOR_DOWNGRADE_SUCCESS:
            return { ...state, preparingForDowngrade: false };
        case ActionType.PREPARE_FOR_DOWNGRADE_FAILED:
            return { ...state, preparingForDowngrade: false, prepareForDowngradeError: action.payload };
        
        case ActionType.GET_USER_OWNED_VPS:
            return { ...state, userOwnedVPs: null, userOwnedVPsLoading: true, userOwnedVPsError: null };
        case ActionType.GET_USER_OWNED_VPS_SUCCESS:
            return { ...state, userOwnedVPs: action.payload.vps, userOwnedVPsLoading: false };
        case ActionType.GET_USER_OWNED_VPS_FAILED:
            return { ...state, userOwnedVPsError: action.payload, userOwnedVPsLoading: false };
        
        case ActionType.BULK_UPDATE_VP_KEEP_STATUS:
            console.log('[Reducer] BULK_UPDATE_VP_KEEP_STATUS - Setting updatingVPKeepStatus to true');
            return { ...state, updatingVPKeepStatus: true, updateVPKeepStatusError: null };
        case ActionType.BULK_UPDATE_VP_KEEP_STATUS_SUCCESS:
            console.log('[Reducer] BULK_UPDATE_VP_KEEP_STATUS_SUCCESS - Setting updatingVPKeepStatus to false');
            return { ...state, updatingVPKeepStatus: false };
        case ActionType.BULK_UPDATE_VP_KEEP_STATUS_FAILED:
            console.log('[Reducer] BULK_UPDATE_VP_KEEP_STATUS_FAILED - Setting updatingVPKeepStatus to false, error:', action.payload);
            return { ...state, updatingVPKeepStatus: false, updateVPKeepStatusError: action.payload };
        case ActionType.RESET_VP_SHARE_KEY_SUCCESS: {
            const { vpId, shareKey } = action.payload;
            const isThisVp = (vp) => vp != null && String(vp.vpId) === String(vpId);
            return {
                ...state,
                vp: isThisVp(state.vp) ? { ...state.vp, shareKey } : state.vp,
                vpList: Array.isArray(state.vpList)
                    ? state.vpList.map((vp) => (isThisVp(vp) ? { ...vp, shareKey } : vp))
                    : state.vpList,
            };
        }
        default:
            return { ...state };
    }
}

export default ValueProposition;
